import {BlogLayoutPage} from 'modules/blog/layouts';
import {BlogCategoryPage} from 'modules/blog/pages/BlogCategoryPage';
import {useRouter} from 'next/router';
import {useGetArticlesByCategoryQuery} from 'modules/blog/services';
import {useMemo, useState} from 'react';
import {getArticlesByCategoryController, getArticlesTypesController} from 'modules/blog/controller';
import {INITIAL_ARTICLES_FILTER_PAGINATION} from 'modules/blog/constants';
import {NextPage} from 'next';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {selectArticleTypes} from 'modules/blog/selectors';
import {actionSetIsArticlesByTag, actionSetIsCategoryType} from 'modules/blog/slice';
import {IArticleType, IGetArticlesData} from 'modules/blog/models';
import {withBasicDataServerSide} from 'core/next/controllers';
import {IBreadcrumb} from 'modules/seo/types';

type TPageContentProps = {
    category: string;
    articleTypes?: IArticleType[];
    categoryData?: IGetArticlesData | undefined;
    isLoading: boolean;
    onLoadMore: () => void;
    isLoadMoreAvailable: boolean;
};

const PageContent = ({
    category,
    articleTypes,
    categoryData,
    isLoading,
    onLoadMore,
    isLoadMoreAvailable,
}: TPageContentProps) => {
    const [title, setTitle] = useState('');

    return useMemo(() => {
        const articleTypeData = articleTypes?.find(({code}) => code === category);

        const categoryTitle =
            categoryData?.data?.categories?.find((item) => item.code === category)?.title ||
            articleTypeData?.title ||
            '';

        setTitle(categoryTitle);

        return (
            <BlogCategoryPage
                articles={categoryData?.data?.items}
                category={category}
                categoryId={articleTypeData?.id}
                isDataLoading={isLoading}
                isLoadMoreAvailable={isLoadMoreAvailable}
                onLoadMore={onLoadMore}
                title={title}
            />
        );
    }, [
        articleTypes,
        category,
        categoryData?.data?.items,
        categoryData?.data?.categories,
        isLoadMoreAvailable,
        isLoading,
        onLoadMore,
        title,
    ]);
};

interface IArticlesCategoryPageProps {
    breadcrumbs?: IBreadcrumb[];
}

const ArticlesCategoryPage: NextPage<{
    pageProps: IArticlesCategoryPageProps;
}> = ({pageProps: {breadcrumbs = []}}) => {
    const {
        query: {category},
    } = useRouter();
    const articleTypes = useAppSelector(selectArticleTypes);

    const articleXData = articleTypes?.find(({code}) => code === category);
    const seoCategoryXData = articleXData?.seo;
    const seoTitle = seoCategoryXData?.metaTitle ?? '';
    const seoRobots = seoCategoryXData?.robots ?? '';
    const seoDescription = seoCategoryXData?.metaDescription ?? '';

    const [currentPage, setCurrentPage] = useState(1);

    const {data: categoryData, isLoading} = useGetArticlesByCategoryQuery({
        category: category as string,
        ...INITIAL_ARTICLES_FILTER_PAGINATION,
        pageNumber: currentPage,
    });

    const handleLoadMore = () => setCurrentPage((prev) => prev + 1);

    const isLoadMoreAvailable = useMemo(
        () =>
            categoryData && INITIAL_ARTICLES_FILTER_PAGINATION.pageSize
                ? currentPage * INITIAL_ARTICLES_FILTER_PAGINATION.pageSize <
                  Number(categoryData.pagination?.totalCount)
                : false,
        [currentPage, categoryData],
    );

    return (
        <BlogLayoutPage
            articleTypes={articleTypes}
            breadcrumbs={breadcrumbs}
            description={seoDescription}
            robots={seoRobots}
            title={seoTitle}
        >
            <PageContent
                category={category as string}
                articleTypes={articleTypes}
                categoryData={categoryData}
                isLoading={isLoading}
                onLoadMore={handleLoadMore}
                isLoadMoreAvailable={isLoadMoreAvailable}
            />
        </BlogLayoutPage>
    );
};

export const getServerSideProps = withBasicDataServerSide(async (store, ctx) => {
    store.dispatch(actionSetIsCategoryType(true));
    store.dispatch(actionSetIsArticlesByTag(false));

    const [categoryResult] = await Promise.all([
        getArticlesByCategoryController(store, ctx),
        getArticlesTypesController(store, ctx),
    ]);

    return {
        props: {
            breadcrumbs: categoryResult?.props?.breadcrumbs || [],
        },
    };
});

export default ArticlesCategoryPage;
